import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { FormsModule } from '@angular/forms';
import {
  CONTACT_SHOW_ROUND_OUTCOMES,
  ContactShowRound,
  ContactShowRoundOutcome,
} from '@app/contacts/shared/contact-show-round.model';
import { EventRoleName } from '@app/events/shared/event-role-name.model';
import { EventRoleNameService } from '@app/events/shared/event-role-name.service';
import { ContactShowRoundService } from '@app/contacts/shared/contact-show-round.service';
import { showRoundReviewClasses } from '@app/show-rounds/show-round-review.model';

/**
 * TVG-324/TVG-368: every viewing booked across this contact's events.
 *
 * Viewings are still booked on the event — a viewing needs an event to exist
 * first — and nothing has moved off it. This panel only gathers them onto the
 * contact record so a prospect's whole viewing history reads in one place: one
 * row per booking, each with its date, time, venue, status and the show around
 * person responsible, and each linking back to the event it is booked on.
 *
 * Booking stays on the event and the sales board. TVG-379: each line is its own
 * viewing record, and Edit records who showed them round and the outcome on
 * that line. The heading and the Book a viewing button live here too (TVG-368),
 * rather than on the contact page, so this card is self-contained.
 */
@Component({
  selector: 'app-contact-show-rounds',
  standalone: true,
  imports: [RouterLink, ButtonComponent, FormsModule],
  templateUrl: './contact-show-rounds.component.html',
})
export class ContactShowRoundsComponent implements OnInit, OnChanges {
  @Input({ required: true }) contactId!: number;

  /** Whether the Book a viewing button shows at all — the same gate the rest of the sales flow's booking actions use. */
  @Input() canCreateEvents = false;

  /**
   * TVG-360 (eleven b): the rounds this panel has loaded, handed up so the
   * contact page can name the venues with a viewing booked in one compact line
   * beneath the journey. The line is a glance; this panel stays the detail.
   * Reusing what is already fetched keeps the page to one request for them.
   */
  @Output() showRoundsLoaded = new EventEmitter<ContactShowRound[]>();

  /** TVG-368: the contact page opens the booking dialog; this panel only asks for it. */
  @Output() bookViewing = new EventEmitter<void>();

  protected showRounds: ContactShowRound[] = [];
  protected loading = false;
  protected loadError: string | null = null;

  /** TVG-379: the viewing being edited in place, and its draft values. */
  protected editingId: number | null = null;
  protected draftShowAroundNameId: number | null = null;
  protected draftOutcome: ContactShowRoundOutcome = 'booked';
  protected saving = false;
  protected saveError: string | null = null;
  protected showAroundNames: EventRoleName[] = [];
  protected readonly outcomes = CONTACT_SHOW_ROUND_OUTCOMES;
  private showAroundNamesLoaded = false;

  constructor(
    private showRoundService: ContactShowRoundService,
    private eventRoleNameService: EventRoleNameService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactId'] && !changes['contactId'].firstChange) {
      this.showRounds = [];
      this.load();
    }
  }

  /** Re-read the list after a round has been booked from this page. */
  reload(): void {
    this.load();
  }

  /**
   * TVG-360: a show round line reads its day of the week — "Saturday 24 October
   * 2026" — so a date can be placed at a glance without counting back from the
   * number. Derived from the date itself, the same way every other date on this
   * page is.
   */
  protected formatLongDate(value: string | null): string {
    if (!value) {
      return 'Date TBC';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    const parts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).formatToParts(parsed);
    const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';

    return `${part('weekday')} ${part('day')} ${part('month')} ${part('year')}`.trim();
  }

  /**
   * TVG-368: Tailwind classes for the Status pill. A pending booking reads
   * neutral; Completed reads success, and Cancelled / Did not show keep the
   * colours they read before the Outcome column was folded in here.
   */
  protected statusClasses(showRound: ContactShowRound): string {
    switch (showRound.status) {
      case 'completed':
      case 'turned_up':
        return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
      case 'cancelled':
        return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400';
      case 'did_not_show':
        return 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  }

  /**
   * TVG-368: the amber "N viewings, N venues" tag on the section heading,
   * shown only once a contact has more than one viewing booked across more
   * than one venue — a single venue, however many viewings, is not worth
   * flagging.
   */
  protected get venueTagText(): string | null {
    const viewings = this.showRounds.length;
    const venues = new Set(this.showRounds.map((round) => round.venue?.name ?? 'Venue TBC')).size;

    if (viewings <= 1 || venues <= 1) {
      return null;
    }

    return `${viewings} viewing${viewings === 1 ? '' : 's'}, ${venues} venue${venues === 1 ? '' : 's'}`;
  }

  /**
   * TVG-339: Tailwind classes for a review answer pill. Great and Yes read as
   * good news; Bad and No are the ones worth catching, which is why the
   * question is asked three hours after the viewing rather than never.
   */
  protected reviewClasses(value: string | null): string {
    return showRoundReviewClasses(value);
  }

  /** TVG-379: open one line for editing; the name list is fetched once, on first use. */
  protected startEdit(showRound: ContactShowRound): void {
    this.editingId = showRound.id;
    this.draftShowAroundNameId = showRound.show_around_name_id;
    this.draftOutcome = showRound.outcome;
    this.saveError = null;

    if (!this.showAroundNamesLoaded) {
      this.showAroundNamesLoaded = true;
      this.eventRoleNameService.getByRole('show_around').subscribe({
        next: (response) => (this.showAroundNames = response.data ?? []),
        error: () => (this.showAroundNames = []),
      });
    }
  }

  protected cancelEdit(): void {
    this.editingId = null;
    this.saveError = null;
  }

  protected saveEdit(showRound: ContactShowRound): void {
    if (this.saving) {
      return;
    }

    this.saving = true;
    this.saveError = null;

    this.showRoundService
      .update(showRound.id, {
        show_around_name_id: this.draftShowAroundNameId,
        outcome: this.draftOutcome,
      })
      .subscribe({
        next: (response) => {
          this.saving = false;
          this.editingId = null;
          // Keep the review answers, which the update does not carry.
          this.showRounds = this.showRounds.map((round) =>
            round.id === showRound.id ? { ...response.data, review: round.review } : round,
          );
          this.showRoundsLoaded.emit(this.showRounds);
        },
        error: (error) => {
          this.saving = false;
          this.saveError = error?.error?.message || 'Failed to save the viewing';
        },
      });
  }

  /**
   * A name no longer on the active list stays selectable on the line that
   * already carries it, so opening Edit never silently drops it.
   */
  protected showAroundChoices(showRound: ContactShowRound): { id: number; name: string }[] {
    const choices = this.showAroundNames.map((name) => ({ id: name.id, name: name.name }));
    if (showRound.show_around_name_id !== null && !choices.some((choice) => choice.id === showRound.show_around_name_id)) {
      choices.unshift({ id: showRound.show_around_name_id, name: showRound.show_around_person ?? 'Current person' });
    }

    return choices;
  }

  private load(): void {
    if (!this.contactId) {
      return;
    }

    this.loading = true;
    this.loadError = null;

    this.showRoundService.listForContact(this.contactId).subscribe({
      next: (response) => {
        this.showRounds = response.data ?? [];
        this.loading = false;
        this.showRoundsLoaded.emit(this.showRounds);
      },
      error: (error) => {
        this.loading = false;
        this.loadError = error?.error?.message || 'Failed to load show rounds';
        this.showRoundsLoaded.emit([]);
      },
    });
  }
}
